// src/api/s3Upload.js
import axios from 'axios';
import CONFIG from '../config';

class S3UploadAPI {
  constructor() {
    // For S3 upload, we use base URL WITHOUT /api/v1
    this.api = axios.create({
      baseURL: CONFIG.API.BASE_URL,
    });

    // Add token to all requests
    this.api.interceptors.request.use(
      (config) => {
        const token = CONFIG.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for handling errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('access_token');
          sessionStorage.removeItem('access_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Upload multiple PDFs to S3
  async uploadMultiplePDFs(companyName, files, forceUpload = false) {
    const formData = new FormData();
    formData.append('company_name', companyName);
    formData.append('force_upload', forceUpload);
    
    // Add each file
    files.forEach((file) => {
      formData.append('files', file);
    });

    // IMPORTANT: Use the correct endpoint without /api/v1 prefix
    const response = await this.api.post(
      '/s3-upload/upload-multiple', // Direct endpoint, not /api/v1/s3-upload/upload-multiple
      formData,
      {
        // Don't set Content-Type header for FormData - browser will set it automatically
      }
    );
    return response.data;
  }

  // Upload single PDF to S3
  async uploadSinglePDF(companyName, file, forceUpload = false) {
    return this.uploadMultiplePDFs(companyName, [file], forceUpload);
  }

  // List files for a company in S3
  async listCompanyFiles(companyName, periodType = null) {
    const params = { company_name: companyName };
    if (periodType) {
      params.period_type = periodType;
    }

    const response = await this.api.get(
      '/s3-upload/list-company-files',
      { params }
    );
    return response.data;
  }

  // Test S3 connection
  async testConnection() {
    const response = await this.api.get('/s3-upload/test-connection');
    return response.data;
  }

  // Test if endpoint exists
  async testEndpoint() {
    try {
      const response = await this.api.head('/s3-upload/upload-multiple');
      return { exists: true, status: response.status };
    } catch (error) {
      return { 
        exists: false, 
        status: error.response?.status,
        message: error.message 
      };
    }
  }
}

export default new S3UploadAPI();